/* Registered Blade destination map. Only public catalogue messages from the exact map frame are accepted. */
(() => {
  const mapOrigin = 'https://account.sofly.io';

  function init(root = document) {
    for (const explorer of root.querySelectorAll('[data-sf-store-map]')) {
      if (explorer.dataset.sfInitialised) continue;
      explorer.dataset.sfInitialised = 'true';

      const frame = explorer.querySelector('[data-sf-map-frame]');
      const list = explorer.querySelector('[data-sf-map-results]');
      const count = explorer.querySelector('[data-sf-map-count]');
      const query = explorer.querySelector('[data-sf-map-query]');
      const layer = explorer.querySelector('[data-sf-map-layer]');
      const template = explorer.querySelector('[data-sf-map-result]');
      const airportMap = explorer.dataset.scope === 'airport';
      const info = explorer.querySelector('[data-sf-map-info]');
      const airportChoice = explorer.querySelector('[data-sf-map-airport]');
      const infoTemplate = explorer.querySelector('[data-sf-map-info-product]');
      const europeViewport = airportMap ? { center: [10, 51], zoom: 4.15 } : null;
      let products = [];
      let selected = '';
      let loaded = false;

      const inScope = (product) => airportMap
        ? product.geographicFamily === 'airport'
        : explorer.dataset.scope === 'mission'
          ? ['region', 'landing'].includes(product.geographicFamily)
          : true;
      const matches = () => products.filter((product) => {
        const search = query?.value.toLowerCase().trim() || '';
        return inScope(product)
          && (!layer?.value || product.geographicFamily === layer.value)
          && [product.title, ...product.icao].join(' ').toLowerCase().includes(search);
      });
      const tell = (data) => frame.contentWindow?.postMessage(data, mapOrigin);
      const sendContext = () => tell({
        type: 'sofly-discovery-context',
        handles: matches().map((product) => product.handle),
        selected,
        ...(europeViewport ? { viewport: europeViewport } : {})
      });

      const airportKey = (product) => product.icao[0] || product.handle;
      function renderAirportInfo() {
        if (!info || !infoTemplate) return;
        const product = matches().find((row) => row.handle === selected);
        info.hidden = !product;
        airportChoice.value = product ? airportKey(product) : '';
        if (!product) return;
        const editions = matches().filter((row) => airportKey(row) === airportKey(product));
        const title = product.title.replace(/\s*\(XP\)\s*$/i, '');
        info.querySelector('[data-sf-map-info-title]').textContent = title;
        const content = info.querySelector('[data-sf-map-info-products]');
        content.replaceChildren();
        for (const edition of editions) {
          const row = infoTemplate.content.cloneNode(true);
          row.querySelector('[data-sf-map-info-name]').textContent = edition.title;
          const image = row.querySelector('img');
          if (typeof edition.image === 'string' && /^https:\/\/(cdn\.shopify\.com|sofly\.io)\//.test(edition.image)) {
            image.src = edition.image;
            image.hidden = false;
          }
          const compatibility = explorer.querySelector(`[data-sf-map-edition="${CSS.escape(edition.handle)}"]`);
          if (compatibility) row.querySelector('[data-sf-map-info-compatibility]').append(compatibility.content.cloneNode(true));
          const link = row.querySelector('a');
          link.href = `/products/${edition.handle}`;
          link.setAttribute('aria-label', `View product: ${edition.title}`);
          content.append(row);
        }
        if (count) count.textContent = `${title}. ${editions.length} ${editions.length === 1 ? 'edition' : 'editions'} available.`;
      }

      function renderAirportChoices() {
        if (!airportChoice) return;
        airportChoice.replaceChildren(new Option('Select a map marker or choose an airport', ''));
        const groups = new Map();
        for (const product of matches().filter((row) => row.mapped)) {
          const key = airportKey(product);
          if (!groups.has(key)) groups.set(key, []);
          groups.get(key).push(product);
        }
        for (const [key, editions] of groups) {
          const title = editions[0].title.replace(/\s*\(XP\)\s*$/i, '');
          airportChoice.add(new Option(`${title}${editions.length > 1 ? ` · ${editions.length} editions` : ''}`, key));
        }
        airportChoice.disabled = groups.size === 0;
        renderAirportInfo();
      }

      function render() {
        const rows = matches();
        if (!rows.some((product) => product.handle === selected)) selected = '';
        if (count) {
          count.textContent = airportMap
            ? `${rows.filter((product) => product.mapped).length} mapped airport destinations.`
            : `${rows.length} matching products`;
        }

        if (list && template) {
          list.replaceChildren();
          for (const product of rows) {
            const fragment = template.content.cloneNode(true);
            const button = fragment.querySelector('button');
            const image = fragment.querySelector('img');
            const link = fragment.querySelector('a');
            const code = fragment.querySelector('[data-sf-map-code]');
            button.dataset.sfMapHandle = product.handle;
            button.setAttribute('aria-pressed', String(product.handle === selected));
            fragment.querySelector('[data-sf-map-title]').textContent = product.title;
            fragment.querySelector('[data-sf-map-note]').textContent = (product.geographicFamily === 'airport' ? 'Airport' : product.geographicFamily === 'region' ? 'Explore' : 'Landings') + (product.mapped ? ' · Mapped locations' : ' · Locations not yet available');
            if (code) code.textContent = product.geographicFamily === 'airport' ? (product.icao[0] || 'Airport') : product.geographicFamily === 'region' ? 'Explore' : 'Landing';
            if (product.image && /^https:\/\/(cdn\.shopify\.com|sofly\.io)\//.test(product.image)) {
              image.src = product.image;
              image.hidden = false;
              image.parentElement.hidden = false;
            }
            link.href = `/products/${product.handle}`;
            link.setAttribute('aria-label', `View product: ${product.title}`);
            list.append(fragment);
          }
          if (!rows.length) {
            const empty = document.createElement('p');
            empty.textContent = 'No matching destinations. Clear your search or choose another layer.';
            list.append(empty);
          }
        }
        if (airportMap) renderAirportChoices();
        sendContext();
      }

      function activate(handle) {
        if (!matches().some((product) => product.handle === handle)) return;
        selected = handle;
        // Opening details must preserve the user's map pan and zoom.
        if (airportMap) {
          renderAirportInfo();
          return;
        }
        render();
        list?.querySelector(`[data-sf-map-handle="${CSS.escape(handle)}"]`)?.scrollIntoView({ block: 'nearest', behavior: 'auto' });
      }

      query?.addEventListener('input', render);
      airportChoice?.addEventListener('change', () => {
        const product = matches().find((row) => airportKey(row) === airportChoice.value);
        selected = product?.handle || '';
        renderAirportInfo();
      });
      const closeInfo = () => {
        selected = '';
        renderAirportInfo();
        airportChoice?.focus();
      };
      info?.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') closeInfo();
      });
      layer?.addEventListener('change', render);
      explorer.addEventListener('click', (event) => {
        if (event.target.closest('[data-sf-map-close]')) closeInfo();
        const choose = event.target.closest('[data-sf-map-handle]');
        if (choose) activate(choose.dataset.sfMapHandle);
        const view = event.target.closest('[data-sf-map-view]');
        if (view) {
          explorer.classList.toggle('sf-store-map-list-only', view.dataset.sfMapView === 'list');
          frame.hidden = view.dataset.sfMapView === 'list';
          for (const button of explorer.querySelectorAll('[data-sf-map-view]')) {
            button.setAttribute('aria-pressed', String(button === view));
          }
          tell({ type: 'sofly-discovery-resize' });
        }
        if (event.target.closest('[data-sf-map-fit]')) {
          if (europeViewport) sendContext();
          else tell({ type: 'sofly-discovery-reset' });
        }
      });

      const listener = (event) => {
        if (!explorer.isConnected) {
          window.removeEventListener('message', listener);
          return;
        }
        if (event.origin !== mapOrigin || event.source !== frame.contentWindow) return;
        const data = event.data;
        if (data?.type === 'sofly-discovery-catalogue' && Array.isArray(data.products)) {
          products = data.products
            .filter((product) => product
              && typeof product.handle === 'string'
              && /^[a-z0-9-]{1,150}$/.test(product.handle)
              && typeof product.title === 'string'
              && product.title.length <= 300
              && ['airport', 'region', 'landing'].includes(product.geographicFamily))
            .map((product) => ({
              ...product,
              icao: Array.isArray(product.icao)
                ? product.icao.filter((code) => typeof code === 'string' && code.length <= 10)
                : []
            }));
          loaded = true;
          render();
        }
        if (data?.type === 'sofly-discovery-ready') {
          tell({ type: 'sofly-discovery-request-catalogue' });
          sendContext();
        }
        if (data?.type === 'sofly-discovery-select' && typeof data.handle === 'string') activate(data.handle);
      };
      window.addEventListener('message', listener);
      frame.addEventListener('load', () => tell({ type: 'sofly-discovery-request-catalogue' }));

      const loadFrame = () => {
        const url = new URL(frame.dataset.src);
        url.searchParams.set('theme', document.documentElement.dataset.theme || 'light');
        frame.src = url.href;
      };
      new MutationObserver(loadFrame).observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['data-theme']
      });
      loadFrame();
      setTimeout(() => {
        if (!loaded && explorer.isConnected && count) {
          count.textContent = airportMap
            ? 'The airport map is taking longer to load. Browse airport products to continue.'
            : 'The map catalogue is taking longer to load. Use Browse products to continue.';
        }
      }, 20000);
    }
  }

  init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
